import { useEffect, useState, useRef } from 'react';
import { X, User, Phone, Shield, Calendar, Home, FileText, ChevronDown, Check, AlertCircle, Search, UserPlus } from 'react-feather';
import { useIntake } from '../../Context/IntakeContext';
import { useData } from '../../Context/DataContext';
import { useAuth } from '../../Context/AuthContext';
import NotesForm from '../Forms/NotesForm';
import { IntakeNote } from '../Forms/NotesForm';
import baseUrl from '../../Utils/baseUrl';
import axios from 'axios';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

interface PatientDetailPanelProps {
  isOpen: boolean;
  onClose: () => void;
  patient: any;
}

const PatientDetailPanel = ({ isOpen, onClose, patient }: PatientDetailPanelProps) => {
  const { getIntakeRecords } = useIntake();
  const { currentProfile, allUsers } = useAuth();
  const { getNotes, currentTimeZone, sendNewNotes } = useData();

  const [statusValue, setStatusValue] = useState<string>(patient?.checked_in || 'Pending');
  const [bookedValue, setBookedValue] = useState<string>(patient?.booked || 'Pending');
  const [reasonValue, setReasonValue] = useState<string>(patient?.reason || 'Pending');
  const [facilityValue, setFacilityValue] = useState<string>(patient?.facility || 'Not Set');
  const [insuranceValue, setInsuranceValue] = useState<string>(patient?.insurance || '');
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [coordinatorValue, setCoordinatorValue] = useState<string[]>(
    patient?.coordinator ? patient.coordinator.split(',').filter(Boolean) : []
  );
  const [intakeNotes, setIntakeNotes] = useState<IntakeNote[]>([]);
  const [isEditingInsurance, setIsEditingInsurance] = useState(false);
  const [insuranceInput, setInsuranceInput] = useState<string>(patient?.insurance || '');

  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [isClosing, setIsClosing] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);

  const panelRef = useRef<HTMLDivElement>(null);
  const insuranceInputRef = useRef<HTMLInputElement>(null);

  const statusOptions = ['Pending', 'Active', 'Approved', 'Approved BD', 'Approved RTC', 'Approved LB No RTC', 'Private Pay', 'Denied'];
  const bookedOptions = ['Pending', 'Arrived', 'MIA', 'Booked', 'Booked BD', 'Yellow Stripe', 'Clinically Denied', 'Outsource/Follow Up', 'Ref to Partner'];
  const reasonOptions = ['Pending', 'Lost Contact', 'Unwilling To Travel', 'Booked Out', 'Other - See Notes'];
  const facilityOptions = ['Not Set', 'Beachside', 'Axis'];
  const insuranceOptions = ['Aetna', 'Ambetter', 'Anthem', 'BCBS', 'Cigna', 'Humana', 'Kaiser', 'Medicaid', 'Medicare', 'Molina', 'Oscar', 'United Healthcare', 'Other'];

  useEffect(() => {
    if (patient?.intake_id) {
      getNotes(patient.intake_id, currentTimeZone);
      setStatusValue(patient.checked_in || 'Pending');
      setBookedValue(patient.booked || 'Pending');
      setReasonValue(patient.reason || 'Pending');
      setFacilityValue(patient.facility || 'Not Set');
      setInsuranceValue(patient.insurance || '');
      setInsuranceInput(patient.insurance || '');
      setSelectedDate(normalizeToInputFormat(patient.expected_arrival_date));
      setCoordinatorValue(patient.coordinator ? patient.coordinator.split(',').filter(Boolean) : []);
      setIsEditingInsurance(false);
    }
  }, [patient]);

  useEffect(() => {
    if (isEditingInsurance && insuranceInputRef.current) {
      insuranceInputRef.current.focus();
    }
  }, [isEditingInsurance]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (activeDropdown && !(event.target as Element).closest('.dropdown-container')) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [activeDropdown]);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleClose();
      }
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  // Handle opening animation
  useEffect(() => {
    if (isOpen) {
      // Small delay to trigger CSS transition
      const timer = setTimeout(() => setIsVisible(true), 10);
      return () => clearTimeout(timer);
    } else {
      setIsVisible(false);
    }
  }, [isOpen]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, 300);
  };

  // Format date of birth to readable format (e.g., "July 26, 1991").
  // For date-only strings we format directly from the string — round-tripping
  // through `new Date("1990-05-15")` parses as UTC midnight and rolls back a
  // day when rendered in negative-offset zones like PST.
  const MONTHS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  const formatDateOfBirth = (dateStr: string | null | undefined): string => {
    if (!dateStr) return 'N/A';
    // YYYY-MM-DD from backend DATE columns
    const ymd = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (ymd) {
      const [, yyyy, mm, dd] = ymd;
      const month = MONTHS[parseInt(mm) - 1];
      if (month) return `${month} ${parseInt(dd)}, ${yyyy}`;
    }
    // MM-DD-YYYY (already-formatted backend strings)
    const mdy = dateStr.match(/^(\d{2})-(\d{2})-(\d{4})$/);
    if (mdy) {
      const [, mm, dd, yyyy] = mdy;
      const month = MONTHS[parseInt(mm) - 1];
      if (month) return `${month} ${parseInt(dd)}, ${yyyy}`;
    }
    // Fallback: Flask serializes Python `date` objects as HTTP-date strings
    // like "Tue, 07 Apr 2026 00:00:00 GMT" — which `new Date()` parses as UTC
    // midnight. Read UTC components (NOT local) so PST/PDT doesn't roll the
    // day back. This is safe for any pure-date value sent from the backend.
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return dateStr;
      const month = MONTHS[date.getUTCMonth()];
      return `${month} ${date.getUTCDate()}, ${date.getUTCFullYear()}`;
    } catch {
      return dateStr;
    }
  };

  // Convert any date string to YYYY-MM-DD format for HTML date input
  const normalizeToInputFormat = (dateStr: string | null | undefined): string => {
    if (!dateStr) return '';

    // Check if already in YYYY-MM-DD format
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      return dateStr;
    }

    // Handle MM-DD-YYYY format from backend
    if (/^\d{2}-\d{2}-\d{4}$/.test(dateStr)) {
      const [mm, dd, yyyy] = dateStr.split('-');
      return `${yyyy}-${mm}-${dd}`;
    }

    // Try parsing as a date
    const date = new Date(dateStr);
    if (!isNaN(date.getTime())) {
      const yyyy = date.getFullYear();
      const mm = String(date.getMonth() + 1).padStart(2, '0');
      const dd = String(date.getDate()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
    }

    return '';
  };

  const updateRecordInfo = async (columnName: string, value: string) => {
    // Use local state values instead of patient prop to preserve recent changes
    const data = {
      checked_in: columnName === 'Status' ? value : statusValue,
      booked: columnName === 'Booked' ? value : bookedValue,
      coordinator: coordinatorValue.join(','),
      summary_out: patient.summary_out,
      reason: columnName === 'Reason' ? value : reasonValue,
      facility: columnName === 'Facility' ? value : facilityValue,
      name: patient.name,
      expected_arrival_date: columnName === 'Arriving Date'
        ? (value || null)
        : (selectedDate || null)
    };

    try {
      await axios.patch(`${baseUrl}/intake/update_non_insurance_info/${patient.intake_id}`, data, {
        headers: { 'Content-Type': 'application/json' }
      });
      getIntakeRecords();
    } catch (error) {
      console.error('Failed to update record:', error);
    }
  };

  // Callback to update timestamp when a note is submitted
  const handleNoteSubmit = async () => {
    if (!patient?.intake_id) return;

    // Trigger an update to refresh last_update_booked timestamp
    const data = {
      checked_in: statusValue,
      booked: bookedValue,
      coordinator: coordinatorValue.join(','),
      summary_out: patient.summary_out,
      reason: reasonValue,
      facility: facilityValue,
      name: patient.name,
      expected_arrival_date: selectedDate || null
    };

    try {
      await axios.patch(`${baseUrl}/intake/update_non_insurance_info/${patient.intake_id}`, data, {
        headers: { 'Content-Type': 'application/json' }
      });
    } catch (error) {
      // Silently fail - the note was still created
      console.log('Timestamp update skipped');
    }
  };

  const handleFieldChange = (field: string, value: string) => {
    // Check if changing to Yellow Stripe
    const isChangingToYellowStripe = field === 'Booked' && value === 'Yellow Stripe' && bookedValue !== 'Yellow Stripe';

    switch (field) {
      case 'Status':
        setStatusValue(value);
        break;
      case 'Booked':
        setBookedValue(value);
        break;
      case 'Reason':
        setReasonValue(value);
        break;
      case 'Facility':
        setFacilityValue(value);
        break;
    }
    updateRecordInfo(field, value);
    setActiveDropdown(null);

    // Add note when manually changing to Yellow Stripe
    if (isChangingToYellowStripe) {
      const coordinatorName = getCoordinatorName(patient?.coordinator);
      const today = new Date();
      const dateStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

      const noteData = {
        intake_id: patient.intake_id,
        date: dateStr,
        notes: `${coordinatorName} marked as Yellow Stripe by ${currentProfile?.name || 'Unknown'}`,
        coordinator: currentProfile?.userid
      };
      sendNewNotes(noteData);
    }
  };

  const updateInsurance = async (value: string) => {
    try {
      await axios.patch(`${baseUrl}/intake/update_insurance_info/${patient.intake_id}`,
        {
          insurance: value,
          dob: patient.date_of_birth || '',
          policy: patient.policy_id || ''
        },
        { headers: { 'Content-Type': 'application/json' } }
      );
      setInsuranceValue(value);
      getIntakeRecords();
    } catch (error) {
      console.error('Failed to update insurance:', error);
    }
  };

  const handleInsuranceSelect = (option: string) => {
    setInsuranceInput(option);
    setInsuranceValue(option);
    updateInsurance(option);
    setActiveDropdown(null);
    setIsEditingInsurance(false);
  };

  const handleInsuranceSave = () => {
    if (insuranceInput.trim()) {
      updateInsurance(insuranceInput.trim());
      setIsEditingInsurance(false);
      setActiveDropdown(null);
    }
  };

  const handleInsuranceKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleInsuranceSave();
    } else if (e.key === 'Escape') {
      setIsEditingInsurance(false);
      setInsuranceInput(insuranceValue);
      setActiveDropdown(null);
    }
  };

  const handleInsuranceFocus = () => {
    setIsEditingInsurance(true);
    setActiveDropdown('insurance');
  };

  const filteredInsuranceOptions = insuranceOptions.filter(option =>
    option.toLowerCase().includes(insuranceInput.toLowerCase())
  );

  // Get coordinator name from user ID
  const getCoordinatorName = (coordinatorId: string) => {
    if (allUsers && coordinatorId) {
      const user = allUsers.find((user: any) => user.userid === coordinatorId);
      return user ? user.name : coordinatorId;
    }
    return coordinatorId || 'Unknown';
  };

  // Get coordinator photo from user ID
  const getCoordinatorPhoto = (coordinatorId: string) => {
    if (allUsers && coordinatorId) {
      const user = allUsers.find((user: any) => user.userid === coordinatorId);
      return user?.profile_photo || null;
    }
    return null;
  };

  // Get initials from name for avatar
  const getInitials = (name: string) => {
    if (!name) return '?';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  // Avatar component for coordinator with photo support
  const CoordinatorAvatar = ({ name, visiblePhoto }: { name: string, visiblePhoto?: string | null }) => {
    if (visiblePhoto) {
      return (
        <img
          src={visiblePhoto}
          alt={name}
          className="w-6 h-6 rounded-full object-cover flex-shrink-0"
        />
      );
    }
    return (
      <div className="w-6 h-6 rounded-full bg-gradient-to-br from-accent-teal to-accent-purple flex items-center justify-center flex-shrink-0">
        <span className="text-white text-[10px] font-semibold">{getInitials(name)}</span>
      </div>
    );
  };

  // Check if current user is staff and not already the coordinator
  const isStaffUser = currentProfile?.privileges === 'staff';
  const isAdminOrManager = currentProfile?.privileges === 'admin' || currentProfile?.privileges === 'manager' || currentProfile?.privileges === 'dev';
  const isCurrentUserCoordinator = coordinatorValue.includes(currentProfile?.userid || '');
  const canAssignToSelf = isStaffUser && !isCurrentUserCoordinator && coordinatorValue.length > 0;
  const canReassign = isAdminOrManager;

  // Get list of users for reassignment dropdown
  // Admin, dev, and owner users can see all active users, others see only intake department
  const canSeeAllUsers = currentProfile?.privileges === 'admin' || currentProfile?.privileges === 'dev' || currentProfile?.privileges === 'owner';
  const intakeCoordinators = allUsers?.filter((user: any) =>
    canSeeAllUsers ? user.active : (user.department === 'intake' && user.active)
  ) || [];

  // Toggle coordinator in multi-select (for admin/manager)
  const toggleCoordinator = async (userId: string) => {
    if (!currentProfile?.userid || !patient?.intake_id || !userId) return;

    setIsAssigning(true);

    const isAdding = !coordinatorValue.includes(userId);
    const newCoordinators = isAdding
      ? [...coordinatorValue, userId]
      : coordinatorValue.filter(id => id !== userId);

    const userName = getCoordinatorName(userId);
    const currentUserName = currentProfile.name || 'Unknown';

    try {
      // Update the coordinator field with comma-separated values
      const data = {
        checked_in: statusValue,
        booked: bookedValue,
        coordinator: newCoordinators.join(','),
        summary_out: patient.summary_out,
        reason: reasonValue,
        facility: facilityValue,
        name: patient.name,
        expected_arrival_date: selectedDate || null
      };

      await axios.patch(`${baseUrl}/intake/update_non_insurance_info/${patient.intake_id}`, data, {
        headers: { 'Content-Type': 'application/json' }
      });

      // Update local state immediately for real-time UI update
      setCoordinatorValue(newCoordinators);

      // Log the assignment change in notes
      const today = new Date();
      const dateStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

      const noteData = {
        intake_id: patient.intake_id,
        date: dateStr,
        notes: `[ASSIGNMENT] ${currentUserName} ${isAdding ? 'added' : 'removed'} ${userName} ${isAdding ? 'to' : 'from'} this record`,
        coordinator: currentProfile.userid
      };

      sendNewNotes(noteData);

      // Refresh data
      getIntakeRecords();
      getNotes(patient.intake_id, currentTimeZone);

    } catch (error) {
      console.error('Failed to update coordinators:', error);
    } finally {
      setIsAssigning(false);
    }
  };

  // Assign record to current user (for staff users)
  const assignToMe = async () => {
    if (!currentProfile?.userid || !patient?.intake_id) return;
    if (coordinatorValue.includes(currentProfile.userid)) return; // Already assigned

    setIsAssigning(true);

    const newCoordinators = [...coordinatorValue, currentProfile.userid];
    const currentUserName = currentProfile.name || 'Unknown';

    try {
      // Update the coordinator field - add current user to list
      const data = {
        checked_in: statusValue,
        booked: bookedValue,
        coordinator: newCoordinators.join(','),
        summary_out: patient.summary_out,
        reason: reasonValue,
        facility: facilityValue,
        name: patient.name,
        expected_arrival_date: selectedDate || null
      };

      await axios.patch(`${baseUrl}/intake/update_non_insurance_info/${patient.intake_id}`, data, {
        headers: { 'Content-Type': 'application/json' }
      });

      // Update local state immediately for real-time UI update
      setCoordinatorValue(newCoordinators);

      // Log the assignment in notes
      const today = new Date();
      const dateStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

      const noteData = {
        intake_id: patient.intake_id,
        date: dateStr,
        notes: `[ASSIGNMENT] ${currentUserName} assigned themselves to this record`,
        coordinator: currentProfile.userid
      };

      sendNewNotes(noteData);

      // Refresh data
      getIntakeRecords();
      getNotes(patient.intake_id, currentTimeZone);

    } catch (error) {
      console.error('Failed to assign record:', error);
    } finally {
      setIsAssigning(false);
    }
  };

  const getInsuranceColor = (insurance: string) => {
    const insuranceLower = insurance?.toLowerCase() || '';
    if (insuranceLower.includes('aetna')) {
      return 'bg-purple-500/20 text-purple-400 border-purple-500/30';
    }
    if (insuranceLower.includes('bcbs') || insuranceLower.includes('blue cross')) {
      return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
    }
    if (insuranceLower.includes('cigna')) {
      return 'bg-orange-500/20 text-orange-400 border-orange-500/30';
    }
    if (insuranceLower.includes('united') || insuranceLower.includes('uhc')) {
      return 'bg-sky-500/20 text-sky-400 border-sky-500/30';
    }
    if (insuranceLower.includes('humana')) {
      return 'bg-green-500/20 text-green-400 border-green-500/30';
    }
    if (insuranceLower.includes('kaiser')) {
      return 'bg-red-500/20 text-red-400 border-red-500/30';
    }
    if (insuranceLower.includes('anthem')) {
      return 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30';
    }
    if (insuranceLower.includes('medicare')) {
      return 'bg-teal-500/20 text-teal-400 border-teal-500/30';
    }
    if (insuranceLower.includes('medicaid')) {
      return 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30';
    }
    if (insuranceLower.includes('molina')) {
      return 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30';
    }
    if (insuranceLower.includes('oscar')) {
      return 'bg-pink-500/20 text-pink-400 border-pink-500/30';
    }
    if (insuranceLower.includes('ambetter')) {
      return 'bg-amber-500/20 text-amber-400 border-amber-500/30';
    }
    return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
  };

  const getStatusColor = (status: string) => {
    const statusLower = status?.toLowerCase() || '';
    if (statusLower.includes('approved') || statusLower.includes('booked') || statusLower === 'arrived') {
      return 'bg-green-500/20 text-green-400 border-green-500/30';
    }
    if (statusLower === 'yellow stripe') {
      return 'bg-yellow-500/30 text-yellow-300 border-yellow-400/50';
    }
    if (statusLower === 'pending') {
      return 'bg-orange-500/20 text-orange-400 border-orange-500/30';
    }
    if (statusLower === 'denied' || statusLower === 'mia' || statusLower === 'clinically denied') {
      return 'bg-red-500/20 text-red-400 border-red-500/30';
    }
    if (statusLower === 'private pay') {
      return 'bg-purple-500/20 text-purple-400 border-purple-500/30';
    }
    if (statusLower === 'outsource/follow up') {
      return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
    }
    if (statusLower === 'ref to partner') {
      return 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30';
    }
    return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
  };

  const isEditable = currentProfile?.privileges !== 'staff';
  const canEditStatusBooked = currentProfile?.privileges !== 'staff'; // Only non-staff can change statuses
  const canEditAssignee = currentProfile?.privileges !== 'staff'; // Only non-staff can change assignees
  const canEditFields = true; // All users can edit facility, reason, etc.

  if (!isOpen && !isClosing) return null;

  return (
    <>
      {/* Backdrop - closes panel when clicked */}
      <div
        className={`fixed inset-0 bg-black/60 z-40 transition-opacity duration-300 ${
          isVisible && !isClosing ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={handleClose}
      />
      {/* Panel - Full screen on mobile, side panel on desktop */}
      <div
        ref={panelRef}
        className={`fixed inset-0 md:inset-y-0 md:left-auto md:right-0 h-full w-full md:max-w-xl bg-dark-bg md:border-l border-dark-border shadow-2xl z-50 overflow-hidden flex flex-col transition-transform duration-300 ease-out ${
          isVisible && !isClosing ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        {/* Header */}
        <div className="flex-shrink-0 px-4 md:px-6 py-4 md:py-5 border-b border-dark-border bg-dark-card">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3 md:gap-4 min-w-0">
              <div className="w-12 h-12 md:w-14 md:h-14 rounded-2xl bg-gradient-to-br from-accent-teal to-accent-purple flex items-center justify-center shadow-lg flex-shrink-0">
                <User className="w-6 h-6 md:w-7 md:h-7 text-white" />
              </div>
              <div className="min-w-0">
                <h2 className="text-xl md:text-2xl font-bold text-white truncate">{patient?.name || 'Patient'}</h2>
                <p className="text-gray-400 text-xs md:text-sm mt-0.5 truncate">
                  Policy: {patient?.policy_id || 'N/A'} • Created: {patient?.created_date ? new Date(patient.created_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'N/A'}
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="p-2.5 md:p-2 hover:bg-red-500/20 active:bg-red-500/30 rounded-xl transition-colors group flex-shrink-0"
            >
              <X className="w-5 h-5 text-red-400 group-hover:text-red-300" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-4 md:px-6 py-4 md:py-5 space-y-4 md:space-y-5">
          {/* Status Cards */}
          <div className="grid grid-cols-2 gap-2 md:gap-3">
            {/* Status */}
            <div className="dropdown-container relative">
              <label className="text-xs text-gray-500 uppercase tracking-wide mb-1.5 block">Status</label>
              <button
                onClick={() => canEditStatusBooked && setActiveDropdown(activeDropdown === 'status' ? null : 'status')}
                disabled={!canEditStatusBooked}
                className={`w-full px-3 md:px-4 py-3 md:py-3 rounded-xl border ${getStatusColor(statusValue)} flex items-center justify-between transition-all min-h-[48px] md:min-h-0 ${
                  canEditStatusBooked ? 'hover:scale-[1.02] active:scale-[0.98] cursor-pointer' : 'cursor-default'
                }`}
              >
                <span className="font-medium text-sm md:text-base truncate">{statusValue}</span>
                {canEditStatusBooked && <ChevronDown className={`w-4 h-4 transition-transform flex-shrink-0 ${activeDropdown === 'status' ? 'rotate-180' : ''}`} />}
              </button>
              {activeDropdown === 'status' && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-dark-card border border-dark-border rounded-xl shadow-xl z-10 overflow-hidden animate-fade-in max-h-64 overflow-y-auto">
                  {statusOptions.map((option) => (
                    <button
                      key={option}
                      onClick={() => handleFieldChange('Status', option)}
                      className={`w-full px-4 py-3 md:py-2.5 text-left hover:bg-dark-card-hover active:bg-dark-card-hover transition-colors flex items-center justify-between ${
                        statusValue === option ? 'bg-dark-card-hover' : ''
                      }`}
                    >
                      <span className={`${getStatusColor(option)} px-2 py-0.5 rounded-md text-sm`}>{option}</span>
                      {statusValue === option && <Check className="w-4 h-4 text-accent-teal" />}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Stage */}
            <div className="dropdown-container relative">
              <label className="text-xs text-gray-500 uppercase tracking-wide mb-1.5 block">Stage</label>
              <button
                onClick={() => canEditStatusBooked && setActiveDropdown(activeDropdown === 'booked' ? null : 'booked')}
                disabled={!canEditStatusBooked}
                className={`w-full px-3 md:px-4 py-3 rounded-xl border ${getStatusColor(bookedValue)} flex items-center justify-between transition-all min-h-[48px] md:min-h-0 ${
                  canEditStatusBooked ? 'hover:scale-[1.02] active:scale-[0.98] cursor-pointer' : 'cursor-default'
                }`}
              >
                <span className="font-medium text-sm md:text-base truncate">{bookedValue}</span>
                {canEditStatusBooked && <ChevronDown className={`w-4 h-4 transition-transform flex-shrink-0 ${activeDropdown === 'booked' ? 'rotate-180' : ''}`} />}
              </button>
              {activeDropdown === 'booked' && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-dark-card border border-dark-border rounded-xl shadow-xl z-10 overflow-hidden animate-fade-in max-h-64 overflow-y-auto">
                  {bookedOptions.map((option) => (
                    <button
                      key={option}
                      onClick={() => handleFieldChange('Booked', option)}
                      className={`w-full px-4 py-3 md:py-2.5 text-left hover:bg-dark-card-hover active:bg-dark-card-hover transition-colors flex items-center justify-between ${
                        bookedValue === option ? 'bg-dark-card-hover' : ''
                      }`}
                    >
                      <span className={`${getStatusColor(option)} px-2 py-0.5 rounded-md text-sm`}>{option}</span>
                      {bookedValue === option && <Check className="w-4 h-4 text-accent-teal" />}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Facility */}
            <div className="dropdown-container relative">
              <label className="text-xs text-gray-500 uppercase tracking-wide mb-1.5 block">Facility</label>
              <button
                onClick={() => canEditFields && setActiveDropdown(activeDropdown === 'facility' ? null : 'facility')}
                disabled={!canEditFields}
                className={`w-full px-4 py-3 rounded-xl border border-dark-border bg-dark-card text-white flex items-center justify-between transition-all ${
                  canEditFields ? 'hover:bg-dark-card-hover hover:scale-[1.02] cursor-pointer' : 'cursor-default'
                }`}
              >
                <div className="flex items-center gap-2">
                  <Home className="w-4 h-4 text-gray-400" />
                  <span className="font-medium">{facilityValue}</span>
                </div>
                {canEditFields && <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${activeDropdown === 'facility' ? 'rotate-180' : ''}`} />}
              </button>
              {activeDropdown === 'facility' && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-dark-card border border-dark-border rounded-xl shadow-xl z-10 overflow-hidden animate-fade-in">
                  {facilityOptions.map((option) => (
                    <button
                      key={option}
                      onClick={() => handleFieldChange('Facility', option)}
                      className={`w-full px-4 py-2.5 text-left hover:bg-dark-card-hover transition-colors flex items-center justify-between text-white ${
                        facilityValue === option ? 'bg-dark-card-hover' : ''
                      }`}
                    >
                      <span>{option}</span>
                      {facilityValue === option && <Check className="w-4 h-4 text-accent-teal" />}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Assignee - Multiple Selection */}
            <div className="dropdown-container relative">
              <label className="text-xs text-gray-500 uppercase tracking-wide mb-1.5 block">Assignee</label>
              <button
                onClick={() => canEditAssignee && setActiveDropdown(activeDropdown === 'assignedTo' ? null : 'assignedTo')}
                disabled={!canEditAssignee}
                className={`w-full px-4 py-3 rounded-xl border border-dark-border bg-dark-card text-white flex items-center justify-between transition-all ${
                  canEditAssignee ? 'hover:bg-dark-card-hover hover:scale-[1.02] cursor-pointer' : 'cursor-default'
                }`}
              >
                <div className="flex items-center gap-2">
                  {coordinatorValue.length === 0 ? (
                    <span className="text-gray-400 text-sm">Not assigned</span>
                  ) : coordinatorValue.length === 1 ? (
                    <>
                      <CoordinatorAvatar name={getCoordinatorName(coordinatorValue[0])} visiblePhoto={getCoordinatorPhoto(coordinatorValue[0])} />
                      <span className="font-medium text-sm">{getCoordinatorName(coordinatorValue[0])}</span>
                    </>
                  ) : (
                    <>
                      {/* Stacked avatars for multiple coordinators */}
                      <div className="flex -space-x-2">
                        {coordinatorValue.slice(0, 3).map((id, index) => (
                          <div key={id} className="relative" style={{ zIndex: 3 - index }}>
                            <CoordinatorAvatar name={getCoordinatorName(id)} visiblePhoto={getCoordinatorPhoto(id)} />
                          </div>
                        ))}
                        {coordinatorValue.length > 3 && (
                          <div className="w-6 h-6 rounded-full bg-dark-input border border-dark-border flex items-center justify-center">
                            <span className="text-[10px] text-gray-400">+{coordinatorValue.length - 3}</span>
                          </div>
                        )}
                      </div>
                      <span className="font-medium text-sm">{coordinatorValue.length} assigned</span>
                    </>
                  )}
                </div>
                {canEditAssignee && <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${activeDropdown === 'assignedTo' ? 'rotate-180' : ''}`} />}
              </button>
              {activeDropdown === 'assignedTo' && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-dark-card border border-dark-border rounded-xl shadow-xl z-10 overflow-hidden animate-fade-in max-h-64 overflow-y-auto">
                  {/* Clear All Button */}
                  {coordinatorValue.length > 0 && (
                    <button
                      onClick={async () => {
                        if (isAssigning) return;
                        setIsAssigning(true);
                        try {
                          const data = {
                            checked_in: statusValue,
                            booked: bookedValue,
                            coordinator: '',
                            summary_out: patient.summary_out,
                            reason: reasonValue,
                            facility: facilityValue,
                            name: patient.name,
                            expected_arrival_date: selectedDate || null
                          };
                          await axios.patch(`${baseUrl}/intake/update_non_insurance_info/${patient.intake_id}`, data, {
                            headers: { 'Content-Type': 'application/json' }
                          });
                          setCoordinatorValue([]);
                          const today = new Date();
                          const dateStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
                          sendNewNotes({
                            intake_id: patient.intake_id,
                            date: dateStr,
                            notes: `[ASSIGNMENT] ${currentProfile?.name || 'Unknown'} cleared all assignees`,
                            coordinator: currentProfile?.userid
                          });
                          getIntakeRecords();
                          getNotes(patient.intake_id, currentTimeZone);
                        } catch (error) {
                          console.error('Failed to clear coordinators:', error);
                        } finally {
                          setIsAssigning(false);
                        }
                      }}
                      disabled={isAssigning}
                      className={`w-full px-4 py-2.5 text-left hover:bg-red-500/10 transition-colors flex items-center gap-2 text-red-400 border-b border-dark-border ${
                        isAssigning ? 'opacity-50 cursor-wait' : ''
                      }`}
                    >
                      <X className="w-4 h-4" />
                      <span className="text-sm">Clear All</span>
                    </button>
                  )}
                  {intakeCoordinators.map((user: any) => {
                    const isSelected = coordinatorValue.includes(user.userid);
                    return (
                      <button
                        key={user.userid}
                        onClick={() => toggleCoordinator(user.userid)}
                        disabled={isAssigning}
                        className={`w-full px-4 py-2.5 text-left hover:bg-dark-card-hover transition-colors flex items-center justify-between text-white ${
                          isSelected ? 'bg-accent-teal/10' : ''
                        } ${isAssigning ? 'opacity-50 cursor-wait' : ''}`}
                      >
                        <div className="flex items-center gap-2">
                          <div className={`w-4 h-4 rounded border flex items-center justify-center ${
                            isSelected ? 'bg-accent-teal border-accent-teal' : 'border-gray-500'
                          }`}>
                            {isSelected && <Check className="w-3 h-3 text-white" />}
                          </div>
                          <CoordinatorAvatar name={user.name} visiblePhoto={user.profile_photo} />
                          <span className="text-sm">{user.name}</span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Arrival Date */}
          <div className="bg-dark-card border border-dark-border rounded-xl p-4">
            <label className="text-xs text-gray-500 uppercase tracking-wide mb-2 block">Expected Arrival</label>
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-accent-teal flex-shrink-0" />
              <div className="flex-1">
                <DatePicker
                  selected={selectedDate ? new Date(selectedDate + 'T00:00:00') : null}
                  onChange={(date: Date | null) => {
                    if (date) {
                      const yyyy = date.getFullYear();
                      const mm = String(date.getMonth() + 1).padStart(2, '0');
                      const dd = String(date.getDate()).padStart(2, '0');
                      const dateStr = `${yyyy}-${mm}-${dd}`;
                      setSelectedDate(dateStr);
                      updateRecordInfo('Arriving Date', dateStr);
                    }
                  }}
                  dateFormat="MM/dd/yyyy"
                  placeholderText="Select arrival date"
                  wrapperClassName="w-full"
                  className="w-full bg-dark-input border border-dark-border rounded-lg px-4 py-2.5 text-white focus:outline-none focus:border-accent-teal transition-colors cursor-pointer"
                  calendarClassName="dark-calendar"
                  popperPlacement="bottom-start"
                  showPopperArrow={false}
                />
              </div>
              <button
                onClick={() => {
                  setSelectedDate('');
                  updateRecordInfo('Arriving Date', '');
                }}
                className={`px-3 py-2.5 rounded-lg text-xs font-medium transition-all flex-shrink-0 ${
                  !selectedDate
                    ? 'bg-gray-600 text-gray-300 cursor-default'
                    : 'bg-gray-700 hover:bg-gray-600 text-gray-300 hover:text-white'
                }`}
                title="Clear date - arrival unknown"
              >
                TBD
              </button>
            </div>
            {!selectedDate && (
              <p className="text-xs text-gray-500 mt-2 ml-7">Arrival date not set</p>
            )}
          </div>

          {/* Client Details Card */}
          <div className="bg-dark-card border border-dark-border rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-dark-border bg-gradient-to-r from-accent-teal/10 to-transparent">
              <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                <User className="w-4 h-4 text-accent-teal" />
                Client Details
              </h3>
            </div>
            <div className="p-4 space-y-3">
              <div className="flex items-center justify-between py-2 border-b border-dark-border/50">
                <span className="text-gray-400 text-sm">Created</span>
                <span className="text-white font-medium">{formatDateOfBirth(patient?.created_date)}</span>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-dark-border/50">
                <span className="text-gray-400 text-sm">Date of Birth</span>
                <span className="text-white font-medium">{formatDateOfBirth(patient?.date_of_birth)}</span>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-dark-border/50">
                <span className="text-gray-400 text-sm">Phone</span>
                <span className="text-white font-medium flex items-center gap-2">
                  <Phone className="w-4 h-4 text-gray-500" />
                  {patient?.phone_number || 'N/A'}
                </span>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-dark-border/50">
                <span className="text-gray-400 text-sm">Emergency Contact</span>
                <span className="text-white font-medium">{patient?.emergency_contact || 'Not Found'}</span>
              </div>
              <div className="flex items-center justify-between py-2">
                <span className="text-gray-400 text-sm">Emergency Phone</span>
                <span className="text-white font-medium">{patient?.emergency_number || 'Not Found'}</span>
              </div>
            </div>
          </div>

          {/* Insurance Details Card */}
          <div className="bg-dark-card border border-dark-border rounded-xl">
            <div className="px-4 py-3 border-b border-dark-border bg-gradient-to-r from-accent-purple/10 to-transparent rounded-t-xl">
              <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                <Shield className="w-4 h-4 text-accent-purple" />
                Insurance Information
              </h3>
            </div>
            <div className="p-4 space-y-3">
              {/* Searchable Insurance Provider */}
              <div className="py-2 border-b border-dark-border/50">
                <span className="text-gray-400 text-sm block mb-2">Provider</span>
                <div className="dropdown-container relative">
                  <div className={`flex items-center gap-2 px-4 py-3 rounded-xl border bg-dark-input transition-all ${
                    isEditingInsurance ? 'border-accent-purple' : 'border-dark-border'
                  } ${canEditFields ? 'hover:border-accent-purple/50' : ''}`}>
                    <Search className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    <input
                      ref={insuranceInputRef}
                      type="text"
                      value={insuranceInput}
                      onChange={(e) => {
                        setInsuranceInput(e.target.value);
                        if (!activeDropdown) setActiveDropdown('insurance');
                      }}
                      onFocus={handleInsuranceFocus}
                      onKeyDown={handleInsuranceKeyDown}
                      disabled={!canEditFields}
                      className="flex-1 bg-transparent text-white text-sm focus:outline-none placeholder-gray-500"
                      placeholder="Search insurance provider..."
                    />
                    {insuranceInput && canEditFields && (
                      <button
                        onClick={() => {
                          setInsuranceInput('');
                          insuranceInputRef.current?.focus();
                        }}
                        className="p-1 hover:bg-dark-card-hover rounded transition-colors"
                      >
                        <X className="w-3 h-3 text-gray-400" />
                      </button>
                    )}
                  </div>
                  {activeDropdown === 'insurance' && canEditFields && (
                    <div className="absolute top-full left-0 right-0 mt-2 bg-dark-card border border-dark-border rounded-xl shadow-xl z-10 overflow-hidden animate-fade-in max-h-64 overflow-y-auto">
                      {filteredInsuranceOptions.length > 0 ? (
                        filteredInsuranceOptions.map((option) => (
                          <button
                            key={option}
                            onClick={() => handleInsuranceSelect(option)}
                            className={`w-full px-4 py-2.5 text-left hover:bg-dark-card-hover transition-colors flex items-center justify-between text-white ${
                              insuranceValue === option ? 'bg-dark-card-hover' : ''
                            }`}
                          >
                            <span>{option}</span>
                            {insuranceValue === option && <Check className="w-4 h-4 text-accent-purple" />}
                          </button>
                        ))
                      ) : (
                        <div className="px-4 py-3">
                          <p className="text-gray-400 text-sm mb-2">No matches found</p>
                          {insuranceInput.trim() && (
                            <button
                              onClick={() => handleInsuranceSelect(insuranceInput.trim())}
                              className="w-full px-3 py-2 bg-accent-purple/20 hover:bg-accent-purple/30 rounded-lg text-accent-purple text-sm transition-colors text-left"
                            >
                              Add "{insuranceInput.trim()}" as new provider
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center justify-between py-2">
                <span className="text-gray-400 text-sm">Policy ID</span>
                <span className="text-white font-medium font-mono text-sm">{patient?.policy_id || 'N/A'}</span>
              </div>
            </div>
          </div>

          {/* Notes Section */}
          <div className="bg-dark-card border border-dark-border rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-dark-border bg-gradient-to-r from-accent-gold/10 to-transparent">
              <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                <FileText className="w-4 h-4 text-accent-gold" />
                Notes
              </h3>
            </div>
            <div className="p-4">
              <NotesForm intakeNotes={intakeNotes} intakeId={patient?.intake_id || null} onNoteSubmit={handleNoteSubmit} />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PatientDetailPanel;
